/**
 * What an `apply_spreadsheet_ops` call did to the researcher's spreadsheet.
 *
 * Built ONLY from the tool result (P2-D4), which the backend produced by reading
 * the workbook back after each op. The model's prose above it can misremember
 * or overclaim; this card cannot, so a failed batch says plainly which changes
 * are still in the sheet (a failed batch is never rolled back), which did not
 * come out as expected and why, and which never ran.
 *
 * When the batch changed the workbook and carries an undo token, the card
 * offers Undo (phase 3, `SpreadsheetUndoAction`), which reverts the whole
 * batch through the backend.
 *
 * `summary`, `detail` and `problem` are written by backend code and never hold
 * a cell value or formula, but they can name a sheet or a range: rendered here,
 * never logged and never sent to analytics.
 */

import { Box, Button, HStack, List, ListItem, Text, VStack } from '@chakra-ui/react';
import { useId, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import type { SpreadsheetChange } from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { ROUTES } from '../../../constants/routes';
import type { SpreadsheetChangeCardData } from '../../../services/agentChat.service';
import { safeAnalytics } from '../../../utils/analytics';
import { MaterialIcon } from '../../ui/MaterialIcon';
import type { InternalLinkPolicy } from './EntityLink';
import { SpreadsheetUndoAction } from './SpreadsheetUndoAction';
import {
  headline,
  isFailed,
  location,
  OTHER_OUTCOME_COPY,
  OUTCOME_COPY,
  OUTCOME_ORDER,
  outcomeCopy,
} from './spreadsheetChangeCardCopy';

/** More changes than this and the list folds behind a toggle. */
const COLLAPSE_THRESHOLD = 5;

function ChangeLine({ change }: { readonly change: SpreadsheetChange }) {
  const copy = outcomeCopy(change.outcome);
  const where = location(change);
  return (
    <ListItem display="flex" gap={2} alignItems="flex-start">
      <MaterialIcon name={copy.icon} boxSize={4} color={copy.color} aria-hidden mt="2px" />
      <Box minW={0}>
        <Text fontSize="sm" color="text.primary" overflowWrap="anywhere">
          {change.detail}
          {where && (
            <Text as="span" color="text.muted">
              {` · ${where}`}
            </Text>
          )}
        </Text>
        {/* Secondary, not error red: the group heading already says how it
            went, and a long reason must not outweigh the change itself.
            `break-word` wraps at spaces and splits only an unbreakable run. */}
        {change.problem && (
          <Text fontSize="xs" color="text.secondary" overflowWrap="break-word" mt={0.5}>
            {change.problem}
          </Text>
        )}
      </Box>
    </ListItem>
  );
}

export function AgentSpreadsheetChangeCard({
  card,
  internalLinkPolicy = 'navigate',
}: {
  readonly card: SpreadsheetChangeCardData;
  readonly internalLinkPolicy?: InternalLinkPolicy;
}) {
  const headingId = useId();
  const listId = useId();
  const { pathname } = useLocation();
  const spreadsheetRoute = ROUTES.spreadsheet(card.artifactId);
  // Already on that spreadsheet: a link to the page you are on is noise, and
  // it would count a non-open in the analytics event.
  const onSpreadsheetPage = pathname === spreadsheetRoute;
  const showOpen = internalLinkPolicy === 'navigate' && !onSpreadsheetPage;
  const failed = isFailed(card);
  const landedCount = card.changes.filter((c) => c.outcome === 'landed').length;
  // Nothing landed, nothing to undo.
  const undoToken = landedCount > 0 ? card.undoToken : undefined;
  const collapsible = card.changes.length > COLLAPSE_THRESHOLD;
  // A long applied list starts folded: the headline already says it all went
  // in. A failed card starts open, so what did not land is never hidden.
  const [expanded, setExpanded] = useState(!collapsible || failed);
  // An applied card is all `landed`, so the group headings would only repeat
  // the headline. A failed card is grouped so each change's state is a word,
  // not just an icon color.
  // A change whose outcome this build does not know goes last, never dropped.
  const groups = failed
    ? [
        ...OUTCOME_ORDER.map((outcome) => ({
          key: outcome,
          heading: OUTCOME_COPY[outcome].heading,
          changes: card.changes.filter((c) => c.outcome === outcome),
        })),
        {
          key: 'other',
          heading: OTHER_OUTCOME_COPY.heading,
          changes: card.changes.filter((c) => !OUTCOME_ORDER.includes(c.outcome)),
        },
      ].filter((g) => g.changes.length > 0)
    : [{ key: 'landed', heading: OUTCOME_COPY.landed.heading, changes: card.changes }];

  const openButton = showOpen ? (
    <Button
      as={RouterLink}
      to={spreadsheetRoute}
      size="xs"
      variant="secondary"
      rightIcon={<MaterialIcon name="arrow_forward" boxSize={3.5} color="inherit" />}
      onClick={() =>
        safeAnalytics.track(ANALYTICS_EVENTS.SPREADSHEET_CHANGE_CARD_OPENED, {
          status: card.status,
          change_count: card.changes.length,
          landed_count: landedCount,
        })
      }
    >
      Open spreadsheet
    </Button>
  ) : null;

  return (
    <Box
      role="group"
      aria-labelledby={headingId}
      mt={3}
      p={3}
      borderWidth="1px"
      borderColor={failed ? 'border.warning' : 'border.default'}
      borderRadius="md"
      bg="bg.subtle"
    >
      <VStack align="stretch" spacing={2}>
        <HStack spacing={2} align="start">
          <MaterialIcon
            name={failed ? 'warning' : 'table_chart'}
            boxSize={4}
            color={failed ? 'text.warning' : 'text.muted'}
            aria-hidden
            mt="2px"
          />
          <VStack align="start" spacing={0.5} minW={0}>
            <Text as="h3" id={headingId} fontSize="sm" fontWeight="semibold" color="text.primary">
              {headline(card, failed)}
            </Text>
            {/* On a failed batch the grouped lines below already say what
                happened, each with its own problem, so the summary would say
                it twice. It stays only when there are no lines to show. */}
            {card.summary && (!failed || card.changes.length === 0) && (
              <Text fontSize="xs" color="text.muted">
                {card.summary}
              </Text>
            )}
          </VStack>
        </HStack>
        {collapsible && (
          <Button
            size="xs"
            variant="ghost"
            alignSelf="flex-start"
            aria-expanded={expanded}
            aria-controls={listId}
            leftIcon={
              <MaterialIcon
                name={expanded ? 'expand_less' : 'expand_more'}
                boxSize={4}
                color="inherit"
              />
            }
            onClick={() => {
              if (!expanded) {
                safeAnalytics.track(ANALYTICS_EVENTS.SPREADSHEET_CHANGE_CARD_EXPANDED, {
                  status: card.status,
                  change_count: card.changes.length,
                  landed_count: landedCount,
                });
              }
              setExpanded(!expanded);
            }}
          >
            {expanded ? 'Hide changes' : `Show ${card.changes.length} changes`}
          </Button>
        )}
        {expanded && (
          <VStack id={listId} align="stretch" spacing={2}>
            {groups.map(({ key, heading, changes }) => (
              <Box key={key}>
                {failed && (
                  <Text as="h4" fontSize="xs" fontWeight="semibold" color="text.secondary" mb={1}>
                    {heading}
                  </Text>
                )}
                <List spacing={1}>
                  {changes.map((change) => (
                    <ChangeLine key={change.index} change={change} />
                  ))}
                </List>
              </Box>
            ))}
          </VStack>
        )}
        {/* Actions row: Undo, then Open. */}
        {undoToken ? (
          <SpreadsheetUndoAction
            artifactId={card.artifactId}
            token={undoToken}
            changeCount={card.changes.length}
            landedCount={landedCount}
            onSpreadsheetPage={onSpreadsheetPage}
            internalLinkPolicy={internalLinkPolicy}
            agentOutcome={card.undoOutcome}
          >
            {openButton}
          </SpreadsheetUndoAction>
        ) : (
          openButton && (
            <HStack spacing={2} justify="flex-end">
              {openButton}
            </HStack>
          )
        )}
      </VStack>
    </Box>
  );
}
